import React, { useEffect, useMemo, useState } from "react";

import DataGrid, { CellClickArgs, Column, SortColumn } from "react-data-grid";
import Icon from "../icon/Icon";
import LoadingIndicator from "../loading/LoadingIndicator";
import { TableQuery, TableSortQuery } from "../../common/models";
import ReactPaginate from "react-paginate";
import EmptyPlaceholder from "../empty/EmptyPlaceholder";
import { setTableLastColumnMaxWidth } from "../../helpers/tableHelpers";

export type ExtendedColumn<T> = Column<T> & { hidden?: boolean };

interface Props<T, TQuery extends TableSortQuery = TableSortQuery> {
  columns: ExtendedColumn<T>[];
  data: T[];
  queryParams?: TQuery;
  onTableQueryChange?: (queryParams: TQuery) => void;
  loading?: boolean;
  hidePagination?: boolean;
  pageCount?: number;
  detailMode?: boolean;
  renderItemActions?: (item: T) => React.ReactNode;
  emptyPlaceholder?: string;
  onRowClick?: (row: T) => void;
  disableExtendToFullWidth?: boolean; // set to true when we have enought columns to extend full width
}

interface WithId {
  id: string | number;
}

const hasPagination = (
  queryParams?: TableSortQuery,
): queryParams is TableQuery => {
  const paginatedQuery = queryParams as Partial<TableQuery> | undefined;

  return (
    typeof paginatedQuery?.page === "number" &&
    typeof paginatedQuery.pageSize === "number"
  );
};

function Table<
  T extends WithId,
  TQuery extends TableSortQuery = TableSortQuery,
>(props: Props<T, TQuery>): React.ReactElement {
  const [sortColumns, setSortColumns] = useState<SortColumn[]>([]);
  const paginatedQueryParams = hasPagination(props.queryParams)
    ? props.queryParams
    : undefined;

  useEffect(() => {
    if (props.queryParams?.sortBy && props.queryParams.sortOrder) {
      setSortColumns([
        {
          columnKey: props.queryParams.sortBy,
          direction: props.queryParams.sortOrder === "ascend" ? "ASC" : "DESC",
        },
      ]);
    } else {
      setSortColumns([]);
    }
  }, [props.queryParams]);

  const handleSortColumnsChange = (columns: SortColumn[]) => {
    const sorter = columns[0];

    if (!props.queryParams) return;

    if (sorter) {
      props.onTableQueryChange?.({
        ...props.queryParams,
        sortBy: sorter.columnKey,
        sortOrder: sorter.direction === "ASC" ? "ascend" : "descend",
      } as TQuery);
    } else {
      props.onTableQueryChange?.({
        ...props.queryParams,
        sortBy: undefined,
        sortOrder: undefined,
      } as TQuery);
    }
  };

  const handlePaginationChange = (page: number) => {
    if (!paginatedQueryParams) return;

    props.onTableQueryChange?.({
      ...paginatedQueryParams,
      page,
    } as TQuery);
  };

  const handleCellClick = ({ row }: CellClickArgs<NoInfer<T>>) => {
    if (!props.onRowClick) return;
    props.onRowClick(row);
  };

  const showActionColumn = props.renderItemActions && props.data.length > 0;

  const visibleColumns = useMemo(
    () => props.columns.filter((x) => !x.hidden),
    [props.columns],
  );

  return props.loading ? (
    <LoadingIndicator />
  ) : (
    <>
      <div
        className={`Table flex ${props.detailMode ? "detailMode" : ""} ${props.onRowClick ? "selectable" : ""} ${showActionColumn ? "action-visible" : ""}`}
      >
        <DataGrid
          onCellClick={handleCellClick}
          columns={setTableLastColumnMaxWidth(visibleColumns)}
          rows={props.data}
          className={`ReactGrid ${props.disableExtendToFullWidth ? "" : "flex-1"}`}
          headerRowHeight={38}
          rowHeight={44}
          // defaultColumnOptions={{
          //   width: !props.detailMode ? "max-content" : "auto",
          // }}
          // defaultColumnOptions={{
          //   width: "max-content",
          // }}
          renderers={{
            renderSortStatus: (props) => {
              if (props.sortDirection) {
                return props.sortDirection === "DESC" ? (
                  <Icon name="chevron-down" />
                ) : (
                  <Icon name="chevron-up" />
                );
              }
            },
            noRowsFallback: (
              <EmptyPlaceholder description={props.emptyPlaceholder} />
            ),
          }}
          sortColumns={sortColumns}
          onSortColumnsChange={(columns) =>
            handleSortColumnsChange(columns.slice(-1))
          }
        />

        {showActionColumn && (
          <div className="action-column">
            <div className="action-header-cell" />
            {props.data.map((item) => (
              <div className="action-cell" key={item.id}>
                {props.renderItemActions!(item)}
              </div>
            ))}
          </div>
        )}
      </div>
      {!props.hidePagination &&
      paginatedQueryParams &&
      props.pageCount &&
      props.pageCount > 1 ? (
        <div className="flex justify-end">
          <ReactPaginate
            onPageChange={(item) => {
              handlePaginationChange(item.selected + 1);
            }}
            initialPage={paginatedQueryParams.page - 1}
            pageCount={props.pageCount ?? 0}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            nextLabel={<Icon name="chevron-right" />}
            previousLabel={<Icon name="chevron-left" />}
            className="mt-2 flex items-center text-[13px]/7"
            pageClassName="px-2  mx-[3px]"
            activeClassName="bg-gray50 rounded-lg"
            breakClassName="mx-[3px]"
          />
        </div>
      ) : null}
    </>
  );
}

export default Table;
